<template>
    <div
        class="relative z-[1] min-h-60 items-center flex xxs:py-4 lg:py-0"
        :class="cover ? 'bg-cover bg-center bg-no-repeat w-full lg:rounded' : 'bg-primary-1 lg:rounded'"
        :style="`background-image: url('${useImagePathFormatter(organization.slug, cover)}');`"
    >
        <div
            class="flex justify-between w-full xxs:px-5 md:px-6 box-border"
            :class="organizationTitleColorPickerVisibility && 'z-[3]'"
        >
            <div class="flex xxs:flex-col lg:flex-row xxs:items-start lg:items-center relative">
                <div
                    v-if="hasLogo"
                    class="w-40 h-40 rounded-full flex items-center justify-center relative z-[0]"
                >
                    <Image
                        :src="useImagePathFormatter(organization.slug, logo)"
                        alt="logo"
                        class="object-contain w-40 h-40"
                        roundness="rounded-full"
                    />
                </div>

                <div v-else class="xxs:w-20 xxs:h-20 lg:w-40 lg:h-40 rounded-full flex items-center justify-center bg-black shrink-0">
                    <DefaultOrgIcon :color="defaultIconColor" />
                </div>

                <p
                    class="text-body-2 md:text-body-1 font-medium lg:ml-8 xxs:mt-3 lg:mt-0 xxs:max-w-full lg:max-w-[70%] inline-block"
                    :class="[organizationTitleColorPickerVisibility && 'pulse-paused', organization.name.length > 100 ? 'lg:text-display-6' : 'lg:text-display-5']"
                    :style="colorObject"
                >
                    {{ organization.name }}
                </p>

                <ColorPicker
                    v-if="organizationTitleColorPickerVisibility"
                    class="xxs:left-auto xxs:right-0 xxs:top-[60px] lg:translate-x-0 lg:left-auto lg:right-0 lg:top-1/2 absolute xxs:max-w-[270px] lg:max-w-[300px]"
                    :loading="loaderPicker"
                    :show-title-icon="false"
                    @save="handleChangeOrganizationTitleColor"
                    @change="previewOrganizationTitleColor"
                    @close="closeOrganizationTitleColorPicker"
                >
                    Change organization's title color
                </ColorPicker>
            </div>
        </div>
        <DashboardEdit
            class="absolute top-0 right-0 p-4"
            :class="organizationTitleColorPickerVisibility && 'z-[1]'"
            v-if="isAdmin"
            @close="showSettingsDropdown = false"
            @showCoverTextColorModal="showCoverTextColorModal"
        />
    </div>
</template>

<script setup>
import {useImagePathFormatter} from "@/composables/useImagePathFormatter";
import {useAuth} from "@/stores/AuthStore";
import {storeToRefs} from "pinia";
import Image from "@/components/custom/Image.vue";
import DefaultOrgIcon from "@/components/icons/small/DefaultOrgIcon.vue";
import DashboardEdit from "@/components/menus/DashboardEdit.vue";
import {useTheme} from "@/stores/ThemeStore";
import ColorPicker from "@/components/custom/ColorPicker.vue";
import {computed, ref} from "vue";
import {useChangeOrganizationTitleColor} from "@/composables/useChangeOrganizationTitleColor";
import {toast} from "vue3-toastify";

const {getContrastingColor} = useTheme();
const {color, cover, logo} = storeToRefs(useTheme());

const {organization} = storeToRefs(useAuth());
const {isAdmin} = useAuth();

const showSettingsDropdown = ref(false);

const organizationTitleColorPickerVisibility = ref(false);
const initialOrganizationTitleColor = ref(organization.value.theme_settings.banner_text_color.hex);

const {changeOrganizationTitleColor} = useChangeOrganizationTitleColor();
const loaderPicker = ref(false);

function showCoverTextColorModal() {
    organizationTitleColorPickerVisibility.value = true;
}

async function handleChangeOrganizationTitleColor(color) {
    loaderPicker.value = true;

    try {
        await changeOrganizationTitleColor(organization.value.slug, color);

        toast.success('Organization title color updated successfully');
    } catch (e) {
        toast.error('Failed to update organization title color')
    }

    organizationTitleColorPickerVisibility.value = false;
    loaderPicker.value = false;
}


function previewOrganizationTitleColor(color) {
    organization.value.theme_settings.banner_text_color.hex = color;
}

function closeOrganizationTitleColorPicker() {
    previewOrganizationTitleColor(initialOrganizationTitleColor.value);
    organizationTitleColorPickerVisibility.value = false;
}

const hasLogo = computed(() => {
    return typeof logo.value != 'undefined' && logo.value !== null;
})

const defaultIconColor = computed(() => {
    return {hex: getContrastingColor(color.value.hex)};
})

const colorObject = computed(() => {
    if(organization.value.theme_settings.banner_text_color || typeof organization.value.theme_settings.banner_text_color != 'undefined') {
        return {color: organization.value.theme_settings.banner_text_color.hex}
    }

    return {color: ''};
})
</script>
